/**
 * Product Bundle Manager
 * Handles bundle product selection, pricing, and cart operations
 */
function _escapeCSS(id) {
  return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id.replace(/([:.\[\]])/g, '\\$1');
}

// Aantal aanbevolen producten dat naast het hoofdproduct getoond wordt.
const BUNDLE_SLOTS = 2;

class ProductBundle {
  constructor(container) {
    this.container = container;
    this.bundleItems = new Map();
    this.mainProduct = null;
    this.productDataCache = new Map();

    this.selectors = {
      bundleItem: '[data-bundle-item]',
      productCard: '.product-card',
      bundleWrapper: '.bundle-product-wrapper',
      actionButton: '[data-bundle-action]',
      mobileAction: '[data-bundle-mobile-action]',
      addToCartBtn: '[data-bundle-add-to-cart]',
      totalPrice: '[data-bundle-total-price]',
      productJson: '[data-product-json]',
      overlay: '[data-bundle-overlay]'
    };

    // Read translatable strings from data attributes
    const d = this.container.dataset;
    this.t = {
      selectVariant: d.tSelectVariant || 'SELECTEER VARIANT',
      add: d.tAdd || 'VOEG TOE',
      remove: d.tRemove || 'VERWIJDEREN',
      soldOut: d.tSoldOut || 'UITVERKOCHT',
      confirm: d.tConfirm || 'BEVESTIGEN',
      itemSingular: d.tItemSingular || 'item',
      itemPlural: d.tItemPlural || 'items',
      addedNotification: d.tAddedNotification || 'toegevoegd aan bundel',
      removedNotification: d.tRemovedNotification || 'Product verwijderd uit bundel',
      cartSuccess: d.tCartSuccess || 'Bundel toegevoegd aan winkelwagen!',
      cartError: d.tCartError || 'Er is iets misgegaan. Probeer het opnieuw.',
      noRecommendations: d.tNoRecommendations || 'Geen aanbevolen producten beschikbaar',
      unavailable: d.tUnavailable || 'Dit product is niet beschikbaar'
    };

    this.init();
  }

  async init() {
    this.setupMainProduct();

    if (this.container.dataset.useRecommendations === 'true') {
      await this.loadRecommendations();
    }

    this.initializeDefaultVariants();
    this.bindEvents();
    this.updateTotalPrice();
    this.adjustSummaryWidth();

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.adjustSummaryWidth(), 250);
    });
  }

  /**
   * Filter out variants marked as hidden in shop
   */
  getVisibleVariants(variants) {
    return variants.filter(v => v.hideinshop !== true);
  }

  /**
   * Initialize all products in bundle with highest price variants (pre-selected)
   */
  initializeDefaultVariants() {
    const allBundleItems = this.container.querySelectorAll('[data-bundle-item]');

    allBundleItems.forEach(bundleItem => {
      const productId = bundleItem.dataset.productId;
      const productData = this.getProductData(bundleItem);

      if (!productData || productData.variants.length === 0) return;

      const visibleVariants = this.getVisibleVariants(productData.variants);
      if (visibleVariants.length === 0) return;

      const availableVariants = visibleVariants.filter(v => v.available);
      const highestPriceVariant = (availableVariants.length > 0 ? availableVariants : visibleVariants)
        .reduce((max, v) => v.price > max.price ? v : max, (availableVariants.length > 0 ? availableVariants : visibleVariants)[0]);

      if (!this.bundleItems.has(parseInt(productId))) {
        this.bundleItems.set(parseInt(productId), {
          id: productData.id,
          title: productData.title,
          price: highestPriceVariant.price,
          prelaunchPct: this.prelaunchPct(bundleItem),
          variantId: highestPriceVariant.id,
          variantTitle: highestPriceVariant.title,
          quantity: 1,
          image: highestPriceVariant.featured_image
        });
      }

      this.updateProductCardState(bundleItem, 'selected', highestPriceVariant);
    });
  }

  /**
   * Setup main product (always included in bundle)
   */
  setupMainProduct() {
    const mainItem = this.container.querySelector('[data-is-main="true"]');
    if (!mainItem) return;

    const productData = this.getProductData(mainItem);
    if (!productData || productData.variants.length === 0) return;

    const visibleVariants = this.getVisibleVariants(productData.variants);
    if (visibleVariants.length === 0) return;

    const availableMain = visibleVariants.filter(v => v.available);
    const mainPool = availableMain.length > 0 ? availableMain : visibleVariants;
    const highestPriceVariant = mainPool.reduce((max, v) =>
      v.price > max.price ? v : max, mainPool[0]);

    this.mainProduct = {
      id: productData.id,
      title: productData.title,
      price: highestPriceVariant.price,
      prelaunchPct: this.prelaunchPct(mainItem),
      variantId: highestPriceVariant.id,
      variantTitle: highestPriceVariant.title,
      quantity: 1,
      image: highestPriceVariant.featured_image
    };

    this.bundleItems.set(productData.id, this.mainProduct);
  }

  /**
   * Load product recommendations from Shopify API
   */
  async loadRecommendations() {
    const productId = this.container.dataset.mainProductId;
    const loadingContainer = this.container.querySelector('[data-bundle-loading]');

    if (!productId) return;

    try {
      const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=10&intent=related`);

      if (!response.ok) throw new Error('Failed to fetch recommendations');

      const data = await response.json();
      const recommendations = data.products || [];

      // Get main product cat- tags for filtering
      const mainCatTags = (this.container.dataset.mainProductTags || '').split(',').filter(t => t.startsWith('cat-'));

      // Filter out main product, bundles (by tag), NZVT (by title), then cat- tags
      const withoutMain = recommendations
        .filter(rec => rec.id.toString() !== productId)
        .filter(rec => {
          // Exclude products with "bundle" tag
          const recTags = Array.isArray(rec.tags) ? rec.tags : (rec.tags || '').split(',');
          const hasBundle = recTags.some(t => t.trim().toLowerCase() === 'bundle');
          if (hasBundle) return false;
          // Exclude products with "NZVT" in title
          if ((rec.title || '').toUpperCase().includes('NZVT')) return false;
          // Also exclude by product type
          const type = (rec.product_type || rec.type || '').toLowerCase();
          if (type === 'bundel' || type === 'nzvt') return false;
          return true;
        });
      const catFiltered = withoutMain
        .filter(rec => {
          if (mainCatTags.length === 0) return true;
          const recTags = Array.isArray(rec.tags) ? rec.tags : (rec.tags || '').split(',');
          const recCatTags = recTags.map(t => t.trim()).filter(t => t.startsWith('cat-'));
          return !recCatTags.some(t => mainCatTags.includes(t));
        });

      // Pak een ruimere kandidatenpool dan de 2 zichtbare plekken: verborgen
      // producten (seo.hidden / hideinshop) vallen pas bij het ophalen van de
      // kaart af, en met reserve blijft de rij dan toch gevuld.
      const CANDIDATE_POOL = BUNDLE_SLOTS + 4;
      const filteredRecommendations = this.pickRandom(catFiltered, CANDIDATE_POOL);

      // Fallback: if not enough after filtering, fill with remaining
      if (filteredRecommendations.length < CANDIDATE_POOL) {
        const remaining = withoutMain
          .filter(rec => !filteredRecommendations.some(f => f.id === rec.id));
        filteredRecommendations.push(...this.pickRandom(remaining, CANDIDATE_POOL - filteredRecommendations.length));
      }

      if (filteredRecommendations.length === 0) {
        if (loadingContainer) {
          loadingContainer.innerHTML = `<p style="text-align: center; padding: 20px; color: #666;">${this.t.noRecommendations}</p>`;
        }
        return;
      }

      const renderedCount = await this.renderRecommendedProducts(filteredRecommendations);

      // Bleef er na het wegfilteren van verborgen producten niets over, toon
      // dan de "geen aanbevelingen"-melding i.p.v. een lege rij.
      if (renderedCount === 0) {
        if (loadingContainer) {
          loadingContainer.innerHTML = `<p style="text-align: center; padding: 20px; color: #666;">${this.t.noRecommendations}</p>`;
        }
        return;
      }

      if (loadingContainer) {
        loadingContainer.remove();
      }

    } catch (error) {
      console.error('[Bundle] Error loading recommendations:', error);
      if (loadingContainer) {
        loadingContainer.innerHTML = `<p style="text-align: center; padding: 20px; color: #666;">${this.t.noRecommendations}</p>`;
      }
    }
  }

  /**
   * Render recommended products in the bundle grid
   */
  async renderRecommendedProducts(products) {
    const grid = this.container.querySelector('.product-bundle__grid');
    if (!grid) return 0;

    // Haal kaarten in kleine batches op tot er BUNDLE_SLOTS zichtbare zijn.
    // Verborgen producten leveren null en worden overgeslagen; in het normale
    // geval (niets verborgen) is dit precies één ronde, dus de reserve-
    // kandidaten kosten alleen extra requests wanneer er echt iets afvalt.
    const valid = [];
    let cursor = 0;
    while (valid.length < BUNDLE_SLOTS && cursor < products.length) {
      const batch = products.slice(cursor, cursor + (BUNDLE_SLOTS - valid.length));
      cursor += batch.length;
      const results = await Promise.all(batch.map(product => this.createProductCardHTML(product)));
      results.forEach(result => { if (result) valid.push(result); });
    }

    valid.slice(0, BUNDLE_SLOTS).forEach(result => {
      const { productData, cardHTML } = result;

      const bundleItem = document.createElement('div');
      bundleItem.className = 'product-bundle__item';
      bundleItem.dataset.bundleItem = '';
      bundleItem.dataset.productId = productData.id;
      bundleItem.dataset.hasVariants = productData.variants.length;
      bundleItem.dataset.dynamicProduct = 'true';
      bundleItem.innerHTML = cardHTML;
      grid.appendChild(bundleItem);
    });

    return Math.min(valid.length, BUNDLE_SLOTS);
  }

  /**
   * Create product card HTML for recommended products
   */
  async createProductCardHTML(product) {
    try {
      const response = await fetch(`/products/${product.handle}?view=bundle-card`);

      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Verborgen product (seo.hidden / hideinshop): de view levert bewust
        // geen kaart. Hier hard stoppen — anders bouwt de fallback hieronder
        // er alsnog een kaart van uit de recommendations-JSON.
        if (doc.querySelector('[data-bundle-hidden]')) return null;

        const cardEl = doc.querySelector('[data-bundle-card]');

        if (cardEl) {
          const jsonScript = cardEl.querySelector('[data-product-json]');
          if (jsonScript) {
            const productData = JSON.parse(jsonScript.textContent);
            return { productData, cardHTML: cardEl.outerHTML };
          }
        }
      }
    } catch (error) {
      console.error('[Bundle] Error fetching product card:', error);
    }

    // Fallback: create basic card HTML
    const firstVariant = product.variants[0];
    const image = product.featured_image || '';

    const cardHTML = `
      <div class="bundle-product-wrapper" data-bundle-card>
        <div class="product-card bundle-native-card">
          <div class="product-card__inner shape shape--square">
            <div class="product-card__images-wrapper">
              <div class="product-card__images">
                <img class="product-card__img shape__target-image" src="${image}" alt="${product.title}" loading="lazy">
              </div>
            </div>
          </div>
          <div class="product-card__details">
            <div class="product-card__details-inner">
              <a class="product-card__title unstyled-link" href="/products/${product.handle}">${product.title}</a>
              <div class="product-card__price">
                <div class="product-card__price-item">${this.formatMoney(firstVariant.price)}</div>
              </div>
            </div>
          </div>
          <button type="button" class="bundle-btn" data-bundle-native-action="add">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5V19M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <script type="application/json" data-product-json>
            ${JSON.stringify(product)}
          </script>
        </div>
      </div>
    `;

    return { productData: product, cardHTML };
  }

  /**
   * Bind all event listeners using event delegation
   */
  bindEvents() {
    this.container.addEventListener('click', (e) => {
      const actionBtn = e.target.closest(this.selectors.actionButton);
      if (actionBtn) {
        e.preventDefault();
        this.handleActionClick(actionBtn);
      }

      const mobileBtn = e.target.closest(this.selectors.mobileAction);
      if (mobileBtn) {
        e.preventDefault();
        this.handleMobileAction(mobileBtn);
      }

      const nativeBtn = e.target.closest('[data-bundle-native-action]');
      if (nativeBtn) {
        e.preventDefault();
        this.handleNativeButtonClick(nativeBtn);
      }

      const editBtn = e.target.closest('[data-bundle-variant-edit]');
      if (editBtn) {
        e.preventDefault();
        this.handleEditVariantClick(editBtn);
      }

      const pillsContainer = e.target.closest('[data-bundle-variant-pills]');
      if (pillsContainer && !e.target.closest('[data-bundle-variant-edit]')) {
        e.preventDefault();
        const bundleItem = pillsContainer.closest(this.selectors.bundleItem);
        if (bundleItem) {
          const productData = this.getProductData(bundleItem);
          if (productData) {
            this.openVariantSelector(bundleItem, productData);
          }
        }
      }
    });

    const addToCartBtn = this.container.querySelector(this.selectors.addToCartBtn);
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', () => this.addBundleToCart());
    }
  }

  handleActionClick(button) {
    const action = button.dataset.bundleAction;
    const bundleItem = button.closest(this.selectors.bundleItem);
    const productData = this.getProductData(bundleItem);
    if (!productData) return;

    if (action === 'add') {
      if (productData.variants.length > 1) {
        this.openVariantSelector(bundleItem, productData);
      } else {
        this.addProductToBundle(bundleItem, productData, productData.variants[0]);
      }
    } else if (action === 'remove') {
      this.removeProductFromBundle(bundleItem, productData.id);
    }
  }

  handleMobileAction(button) {
    const action = button.dataset.action;
    const bundleItem = button.closest(this.selectors.bundleItem);
    const productData = this.getProductData(bundleItem);
    if (!productData) return;

    if (action === 'add') {
      this.openVariantSelector(bundleItem, productData);
    } else if (action === 'remove') {
      this.removeProductFromBundle(bundleItem, productData.id);
    }
  }

  handleNativeButtonClick(button) {
    const action = button.dataset.bundleNativeAction;
    const bundleItem = button.closest(this.selectors.bundleItem);
    const productData = this.getProductData(bundleItem);
    if (!productData) return;

    if (action === 'add') {
      if (productData.variants.length > 1) {
        this.openVariantSelector(bundleItem, productData);
      } else {
        this.addProductToBundle(bundleItem, productData, productData.variants[0]);
      }
    } else if (action === 'remove') {
      this.removeProductFromBundle(bundleItem, productData.id);
    }
  }

  handleEditVariantClick(button) {
    const bundleItem = button.closest(this.selectors.bundleItem);
    const productData = this.getProductData(bundleItem);
    if (productData) {
      this.openVariantSelector(bundleItem, productData);
    }
  }

  /**
   * Open variant selector popup
   */
  openVariantSelector(bundleItem, productData) {
    const popup = bundleItem.querySelector('[data-bundle-variant-popup]');
    if (!popup) {
      const availableVariant = productData.variants.find(v => v.available);
      if (availableVariant) {
        this.addProductToBundle(bundleItem, productData, availableVariant);
      } else {
        this.showNotification(this.t.unavailable, 'error');
      }
      return;
    }

    this.initVariantSelector(popup, bundleItem, productData);

    popup.style.display = 'block';
    requestAnimationFrame(() => {
      popup.classList.add('is-active');
    });
    document.body.style.overflow = 'hidden';
  }

  /**
   * Initialize variant selector functionality
   */
  initVariantSelector(popup, bundleItem, productData) {
    const selectedOptions = new Map();
    const confirmBtn = popup.querySelector('[data-bundle-variant-confirm]');
    const closeBtn = popup.querySelector('[data-bundle-variant-popup-close]');
    const scrim = popup.querySelector('[data-bundle-variant-popup-scrim]');
    const optionGroups = popup.querySelectorAll('.bundle-variant-popup__option-group');

    const optionValues = new Map();
    optionGroups.forEach(group => {
      const optionIndex = parseInt(group.dataset.optionIndex);
      const values = new Set();
      group.querySelectorAll('.bundle-variant-popup__pill').forEach(pill => {
        values.add(pill.dataset.optionValue.trim());
      });
      optionValues.set(optionIndex, values);
    });

    // Pre-select variant — use the currently selected variant from the bundle
    const bundleItemData = this.bundleItems.get(productData.id) || this.bundleItems.get(parseInt(productData.id)) || this.bundleItems.get(String(productData.id));
    let variantToPreselect = null;

    // Use stored variant ID or title from bundle selection
    if (bundleItemData?.variantId) {
      variantToPreselect = productData.variants.find(v => String(v.id) === String(bundleItemData.variantId));
    }
    if (!variantToPreselect && bundleItemData?.variantTitle) {
      variantToPreselect = productData.variants.find(v => v.title === bundleItemData.variantTitle);
    }

    // Fallback: highest price available variant
    if (!variantToPreselect) {
      const visibleVariants = this.getVisibleVariants(productData.variants);
      const availableFallback = visibleVariants.filter(v => v.available);
      const fallbackPool = availableFallback.length > 0 ? availableFallback : visibleVariants;
      if (fallbackPool.length > 0) {
        variantToPreselect = fallbackPool.reduce((max, v) =>
          v.price > max.price ? v : max, fallbackPool[0]);
      }
    }

    if (variantToPreselect?.title) {
      variantToPreselect.title.split(' / ').map(v => v.trim()).forEach((value, index) => {
        selectedOptions.set(index, value);
      });
    }

    const pills = popup.querySelectorAll('.bundle-variant-popup__pill');
    const visibleVariants = this.getVisibleVariants(productData.variants);

    // Detect smaak groups
    const smaakKw = ['smaak', 'flavour', 'flavor', 'geschmack', 'kleur', 'color', 'farbe'];
    const isSmaakGroup = (group) => group.classList.contains('bundle-variant-popup__option-group--smaak');
    // Best deal: label-tekst + custom.best_deal (zie assets/best-deal.js)
    const bestDealLabel = popup.getAttribute('data-best-deal-label') || '';
    const bestDealSetting = popup.getAttribute('data-best-deal') || '';

    let _lastChangedIsSmaak = false;
    const updatePillStates = () => {
      optionGroups.forEach(group => {
        const optionIndex = parseInt(group.dataset.optionIndex);
        const groupPills = group.querySelectorAll('.bundle-variant-popup__pill');
        const selectedValue = selectedOptions.get(optionIndex);
        const isSmaak = isSmaakGroup(group);

        groupPills.forEach(pill => {
          const optionValue = pill.dataset.optionValue.trim();
          const isSelected = selectedValue === optionValue;

          if (isSmaak) {
            // Smaak pills: always visible, grey out if all sold out
            const existsAtAll = visibleVariants.some(v => {
              const opts = v.title.split(' / ').map(o => o.trim());
              return opts[optionIndex] === optionValue;
            });
            const hasAvailable = existsAtAll && visibleVariants.some(v => {
              if (!v.available) return false;
              const opts = v.title.split(' / ').map(o => o.trim());
              return opts[optionIndex] === optionValue;
            });

            pill.style.display = existsAtAll ? '' : 'none';
            // Strip any legacy sold-out badge — sold-out state is now
            // communicated purely via the greyed `.variant-unavailable`
            // styling and the disabled CTA.
            const oldBadge = pill.querySelector('.sold-out-badge');
            if (oldBadge) oldBadge.remove();
            if (existsAtAll && !hasAvailable) {
              pill.classList.add('variant-unavailable');
              pill.disabled = false; // keep clickable
            } else if (existsAtAll) {
              pill.classList.remove('variant-unavailable');
              pill.disabled = false;
            }
          } else {
            // Secondary pills: check combo with current selections
            const hasValidCombo = visibleVariants.some(variant => {
              const variantOptions = variant.title.split(' / ').map(v => v.trim());
              if (variantOptions[optionIndex] !== optionValue) return false;
              let _comboMatch = true;
              selectedOptions.forEach((val, idx) => {
                if (idx === optionIndex) return;
                if (variantOptions[idx] !== val) _comboMatch = false;
              });
              if (!_comboMatch) return false;
              return true;
            });

            // Remove old sold-out badge
            const oldSob = pill.querySelector('.sold-out-badge');
            if (oldSob) oldSob.remove();

            if (!hasValidCombo) {
              pill.style.display = 'none';
              pill.classList.remove('variant-unavailable');
            } else {
              pill.style.display = '';
              // Check availability
              const isAvailable = visibleVariants.some(variant => {
                if (!variant.available) return false;
                const variantOptions = variant.title.split(' / ').map(v => v.trim());
                if (variantOptions[optionIndex] !== optionValue) return false;
                let _availMatch = true;
                selectedOptions.forEach((val, idx) => {
                  if (idx === optionIndex) return;
                  if (variantOptions[idx] !== val) _availMatch = false;
                });
                return _availMatch;
              });
              if (isAvailable) {
                pill.classList.remove('variant-unavailable');
                pill.disabled = false;
              } else {
                // Sold-out secondary pill: greyed out but still clickable,
                // no badge — the CTA communicates sold-out state.
                pill.classList.add('variant-unavailable');
                pill.disabled = false;
              }
            }
          }

          if (isSelected) {
            pill.setAttribute('data-selected', 'true');
            pill.classList.add('selected');
          } else {
            pill.removeAttribute('data-selected');
            pill.classList.remove('selected');
          }
        });

        // Best deal badge — logica staat in assets/best-deal.js
        if (!isSmaak && window.cnBestDeal) {
          const optsOf = (v) => (v.title || '').split(' / ').map(o => o.trim());
          window.cnBestDeal.apply(groupPills, {
            label: bestDealLabel,
            setting: bestDealSetting,
            groupName: group.querySelector('.bundle-variant-popup__option-label')?.textContent || group.getAttribute('option-picker-name'),
            variants: productData.variants,
            pillValue: (p) => p.dataset.optionValue.trim(),
            valueOf: (v) => optsOf(v)[optionIndex],
            inContext: (v) => {
              const o = optsOf(v);
              let ok = true;
              selectedOptions.forEach((val, idx) => { if (idx !== optionIndex && o[idx] !== val) ok = false; });
              return ok;
            }
          });
        }
      });

      // Auto-select best available for secondary groups if current is invalid.
      // Sold-out is a valid selection (user explicitly picked it) — only the
      // hidden / non-existing state invalidates the current choice.
      optionGroups.forEach(group => {
        if (isSmaakGroup(group)) return;
        const optionIndex = parseInt(group.dataset.optionIndex);
        const groupPills = group.querySelectorAll('.bundle-variant-popup__pill');
        const curVal = selectedOptions.get(optionIndex);
        const curPill = curVal ? group.querySelector(`[data-option-value="${_escapeCSS(curVal)}"]`) : null;
        const curValid = curPill && curPill.style.display !== 'none';

        if (!curValid || _lastChangedIsSmaak) {
          let bestPill = null;
          let bestNum = -1;
          groupPills.forEach(p => {
            if (p.style.display === 'none' || p.classList.contains('variant-unavailable')) return;
            const text = p.querySelector('.pill__label')?.textContent || '';
            const num = parseFloat(text.replace(/[^\d.,]/g, '').replace(',', '.'));
            if (!isNaN(num) && num > bestNum) { bestNum = num; bestPill = p; }
            else if (isNaN(num) && !bestPill) bestPill = p;
          });
          // Fallback: no available pill for this combo — pick the first
          // visible one (sold-out ok) so a secondary variant is ALWAYS
          // selected, even when the chosen smaak is fully sold out.
          if (!bestPill) {
            groupPills.forEach(p => {
              if (bestPill) return;
              if (p.style.display === 'none') return;
              bestPill = p;
            });
          }
          if (bestPill) {
            groupPills.forEach(p => { p.removeAttribute('data-selected'); p.classList.remove('selected'); });
            selectedOptions.set(optionIndex, bestPill.dataset.optionValue.trim());
            bestPill.setAttribute('data-selected', 'true');
            bestPill.classList.add('selected');
          }
        }
      });

      const allOptionsSelected = optionValues.size === selectedOptions.size;
      const matchingVariant = this.findMatchingVariant(productData.variants, selectedOptions);
      const canConfirm = allOptionsSelected && matchingVariant && matchingVariant.available;

      confirmBtn.disabled = !canConfirm;
      if (allOptionsSelected && matchingVariant && !matchingVariant.available) {
        confirmBtn.textContent = this.t.soldOut;
      } else {
        confirmBtn.textContent = this.t.confirm;
      }
    };

    const handlePillClick = (e) => {
      const pill = e.currentTarget;
      // Sold-out pills are clickable — they get selected, CTA updates to
      // "Uitverkocht" so the user knows but can still choose that variant.
      const optionGroup = pill.closest('.bundle-variant-popup__option-group');
      if (!optionGroup) return;
      const optionValue = pill.dataset.optionValue.trim();
      selectedOptions.set(parseInt(optionGroup.dataset.optionIndex), optionValue);
      _lastChangedIsSmaak = isSmaakGroup(optionGroup);
      updatePillStates();
      _lastChangedIsSmaak = false;

      // Update smaak dropdown trigger if this is a smaak group
      if (optionGroup.classList.contains('bundle-variant-popup__option-group--smaak')) {
        const triggerLabel = optionGroup.querySelector('[data-bundle-smaak-label]');
        const triggerSwatch = optionGroup.querySelector('.bundle-smaak-dropdown__swatch');
        const pillSwatch = pill.querySelector('.pill__swatch');
        if (triggerLabel) triggerLabel.textContent = optionValue;
        if (triggerSwatch && pillSwatch) triggerSwatch.src = pillSwatch.src;
        // Close dropdown
        optionGroup.removeAttribute('data-dropdown-open');
        const list = optionGroup.querySelector('.bundle-smaak-dropdown__list');
        if (list) list.style.display = 'none';
      }
    };

    const handleConfirm = () => {
      const variant = this.findMatchingVariant(productData.variants, selectedOptions);
      if (variant) {
        this.addProductToBundle(bundleItem, productData, variant);
        closePopup();
      }
    };

    const closePopup = () => {
      popup.classList.remove('is-active');
      // Reset smaak dropdown state
      popup.querySelectorAll('.bundle-variant-popup__option-group--smaak').forEach(group => {
        group.removeAttribute('data-dropdown-open');
        const list = group.querySelector('.bundle-smaak-dropdown__list');
        if (list) list.style.display = 'none';
      });
      setTimeout(() => {
        popup.style.display = 'none';
        document.body.style.overflow = '';
      }, 300);

      pills.forEach(pill => pill.removeEventListener('click', handlePillClick));
      confirmBtn.removeEventListener('click', handleConfirm);
      closeBtn.removeEventListener('click', closePopup);
      scrim.removeEventListener('click', closePopup);
      document.removeEventListener('keydown', handleEscape);
    };

    pills.forEach(pill => pill.addEventListener('click', handlePillClick));
    confirmBtn.addEventListener('click', handleConfirm);
    closeBtn.addEventListener('click', closePopup);
    scrim.addEventListener('click', closePopup);

    // Smaak dropdown trigger toggle (guard against duplicate listeners)
    popup.querySelectorAll('[data-bundle-smaak-trigger]').forEach(trigger => {
      if (trigger.dataset.smaakBound) return;
      trigger.dataset.smaakBound = 'true';
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const group = trigger.closest('.bundle-variant-popup__option-group--smaak');
        if (!group) return;
        const list = group.querySelector('.bundle-smaak-dropdown__list');
        if (!list) return;
        const isOpen = group.hasAttribute('data-dropdown-open');
        if (isOpen) {
          group.removeAttribute('data-dropdown-open');
          list.style.display = 'none';
        } else {
          group.setAttribute('data-dropdown-open', '');
          list.style.display = 'flex';
        }
      });
    });

    // Initial update — only force best deal if no variant was pre-selected from bundle
    _lastChangedIsSmaak = !bundleItemData?.variantId;
    updatePillStates();
    _lastChangedIsSmaak = false;

    // Sync smaak/kleur dropdown triggers with pre-selected values and ensure closed
    popup.querySelectorAll('.bundle-variant-popup__option-group--smaak').forEach(function(group) {
      group.removeAttribute('data-dropdown-open');
      var list = group.querySelector('.bundle-smaak-dropdown__list');
      var trigger = group.querySelector('[data-bundle-smaak-trigger]');

      // Count visible pills — if only 1, hide dropdown entirely
      var visiblePills = group.querySelectorAll('.bundle-variant-popup__pill');
      var visibleCount = 0;
      visiblePills.forEach(function(p) { if (p.style.display !== 'none') visibleCount++; });

      if (visibleCount <= 1) {
        // Only 1 variant — show trigger as static (not clickable), hide chevron
        if (trigger) {
          trigger.style.pointerEvents = 'none';
          trigger.style.cursor = 'default';
          var chevron = trigger.querySelector('.bundle-smaak-dropdown__chevron');
          if (chevron) chevron.style.display = 'none';
        }
        if (list) list.style.display = 'none';
      } else {
        if (trigger) {
          trigger.style.pointerEvents = '';
          trigger.style.cursor = '';
          var chevron2 = trigger.querySelector('.bundle-smaak-dropdown__chevron');
          if (chevron2) chevron2.style.display = '';
        }
        if (list) list.style.display = 'none';
      }

      // Update trigger to match the selected pill
      var optionIndex = parseInt(group.dataset.optionIndex);
      var selectedValue = selectedOptions.get(optionIndex);
      if (selectedValue) {
        var triggerLabel = group.querySelector('[data-bundle-smaak-label]');
        var triggerSwatch = group.querySelector('.bundle-smaak-dropdown__swatch');
        var selectedPill = group.querySelector('[data-option-value="' + selectedValue.replace(/"/g, '\\"') + '"]');
        if (triggerLabel) triggerLabel.textContent = selectedValue;
        if (triggerSwatch && selectedPill) {
          var pillSwatch = selectedPill.querySelector('.pill__swatch');
          if (pillSwatch) triggerSwatch.src = pillSwatch.src;
        }
      }
    });

    const handleEscape = (e) => {
      if (e.key === 'Escape') closePopup();
    };
    document.addEventListener('keydown', handleEscape);
  }

  /**
   * Find matching variant based on selected options
   */
  findMatchingVariant(variants, selectedOptions) {
    const selectedValues = Array.from(selectedOptions.values());
    return variants.find(variant => {
      if (variant.hideinshop === true) return false;
      const variantOptions = variant.title.split(' / ').map(v => v.trim());
      return selectedValues.every((value, index) =>
        variantOptions[index] && variantOptions[index] === value
      );
    });
  }

  /**
   * Add product to bundle
   */
  addProductToBundle(bundleItem, productData, variant) {
    this.bundleItems.set(productData.id, {
      id: productData.id,
      title: productData.title,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      prelaunchPct: this.prelaunchPct(bundleItem),
      quantity: 1,
      image: variant.featured_image
    });

    this.updateProductCardState(bundleItem, 'selected', variant);
    this.updateTotalPrice();
    this.showNotification(`${productData.title} ${this.t.addedNotification}`, 'success');
  }

  /**
   * Remove product from bundle
   */
  removeProductFromBundle(bundleItem, productId) {
    this.bundleItems.delete(productId);
    this.updateProductCardState(bundleItem, 'default');
    this.updateTotalPrice();
    this.showNotification(this.t.removedNotification, 'info');
  }

  /**
   * Update product card visual state
   */
  updateProductCardState(bundleItem, state, variant = null) {
    const overlay = bundleItem.querySelector(this.selectors.overlay);
    const actionButton = overlay?.querySelector(this.selectors.actionButton);
    const mobileButton = bundleItem.querySelector(this.selectors.mobileAction);
    const nativeButton = bundleItem.querySelector('[data-bundle-native-action]');
    const variantPills = bundleItem.querySelector('[data-bundle-variant-pills]');
    const hasMultipleVariants = parseInt(bundleItem.dataset.hasVariants) > 1;

    if (state === 'selected') {
      bundleItem.classList.add('product-bundle__item--selected');

      if (variantPills && variant) {
        this.updateVariantPills(variantPills, variant);
      }

      if (actionButton) {
        actionButton.dataset.bundleAction = 'remove';
        actionButton.textContent = this.t.remove;
      }

      if (mobileButton) {
        mobileButton.dataset.action = 'remove';
        mobileButton.innerHTML = this.getTrashIcon();
      }

      if (nativeButton) {
        nativeButton.dataset.bundleNativeAction = 'remove';
        nativeButton.innerHTML = this.getTrashIcon();
        nativeButton.classList.add('bundle-btn--remove');
        nativeButton.setAttribute('aria-pressed', 'true');
      }

      if (variant) {
        this.updateVariantImage(bundleItem, variant);
        this.updateVariantPrice(bundleItem, variant);
      }

    } else {
      bundleItem.classList.remove('product-bundle__item--selected');

      if (variantPills) {
        variantPills.classList.remove('is-visible');
        variantPills.innerHTML = '';
      }

      if (actionButton) {
        actionButton.dataset.bundleAction = 'add';
        actionButton.textContent = hasMultipleVariants ? this.t.selectVariant : this.t.add;
      }

      if (mobileButton) {
        mobileButton.dataset.action = 'add';
        mobileButton.innerHTML = this.getPlusIcon();
      }

      if (nativeButton) {
        nativeButton.dataset.bundleNativeAction = 'add';
        nativeButton.innerHTML = this.getPlusIcon();
        nativeButton.classList.remove('bundle-btn--remove');
        nativeButton.setAttribute('aria-pressed', 'false');
      }
    }
  }

  updateVariantImage(bundleItem, variant) {
    const productCard = bundleItem.querySelector('.product-card__img');
    if (!productCard) return;

    const featuredImage = variant.featured_image;
    const imageUrl = featuredImage && (typeof featuredImage === 'string' ? featuredImage : featuredImage.src);

    if (imageUrl && typeof imageUrl === 'string' && imageUrl.trim() !== '') {
      productCard.removeAttribute('srcset');
      productCard.removeAttribute('data-srcset');
      productCard.src = imageUrl;
    }
  }

  updateVariantPrice(bundleItem, variant) {
    const priceElement = bundleItem.querySelector('[data-bundle-price-item]')
      || bundleItem.querySelector('.product-card__price-item');
    const compareElement = bundleItem.querySelector('[data-bundle-price-compare]');
    const priceCost = bundleItem.querySelector('.product-card__price-cost');
    const saleBadge = bundleItem.querySelector('[data-bundle-sale-badge]');
    const highlightEnabled = priceCost?.dataset.highlightSaleEnabled === 'true';

    const rawPrice = typeof variant.price === 'number' ? variant.price : null;
    const rawCompare = typeof variant.compare_at_price === 'number' ? variant.compare_at_price : null;
    // Pre-launch: gekorte prijs tonen, oorspronkelijke prijs doorgestreept.
    const plPct = this.prelaunchPct(bundleItem);
    const variantPrice = rawPrice !== null ? this.applyPrelaunch(rawPrice, plPct) : null;
    let compareAt = rawCompare;
    if (plPct > 0 && rawPrice !== null && (compareAt === null || compareAt <= rawPrice)) {
      compareAt = rawPrice;
    }
    const hasRealDiscount = compareAt !== null && variantPrice !== null && compareAt > variantPrice;

    if (priceElement && variantPrice !== null) {
      priceElement.textContent = this.formatMoney(variantPrice);
      priceElement.classList.toggle(
        'product-card__price-item--highlight-sale',
        highlightEnabled && hasRealDiscount
      );
    }

    if (compareElement) {
      if (hasRealDiscount) {
        compareElement.textContent = this.formatMoney(compareAt);
        compareElement.removeAttribute('hidden');
      } else {
        compareElement.textContent = '';
        compareElement.setAttribute('hidden', '');
      }
    }

    if (saleBadge) {
      if (hasRealDiscount) {
        const pct = Math.round(((compareAt - variantPrice) / compareAt) * 100);
        const badgeTextNode = saleBadge.querySelector('.product-sale-amount-badge') || saleBadge;
        badgeTextNode.innerHTML = badgeTextNode.innerHTML.replace(/-?\d+\s*%/, `-${pct}%`);
        saleBadge.removeAttribute('hidden');
      } else {
        saleBadge.setAttribute('hidden', '');
      }
    }
  }

  /**
   * Update variant pills display
   */
  updateVariantPills(pillsContainer, variant) {
    if (!variant?.title) {
      pillsContainer.classList.remove('is-visible');
      const editIcon = pillsContainer.querySelector('[data-bundle-variant-edit]');
      pillsContainer.innerHTML = '';
      if (editIcon) pillsContainer.appendChild(editIcon);
      return;
    }

    const variantOptions = variant.title
      .split(' / ')
      .map(v => v.trim())
      .filter(v => v !== 'Default Title');

    if (variantOptions.length === 0) {
      pillsContainer.classList.remove('is-visible');
      const editIcon = pillsContainer.querySelector('[data-bundle-variant-edit]');
      pillsContainer.innerHTML = '';
      if (editIcon) pillsContainer.appendChild(editIcon);
      return;
    }

    const bundleItem = pillsContainer.closest('[data-bundle-item]');

    // Swatch images from server-rendered JSON (fast, no DOM queries)
    let swatchData = {};
    const swatchScript = bundleItem?.querySelector('[data-bundle-swatch-data]');
    if (swatchScript) {
      try { swatchData = JSON.parse(swatchScript.textContent); } catch (e) { /* ignore */ }
    }

    // Reorder: primary option (smaak/kleur) first, then secondary
    const productData = this.getProductData(bundleItem);
    const primaryKeywords = ['smaak', 'flavour', 'flavor', 'geschmack', 'kleur', 'color', 'farbe'];
    let sortedOptions = variantOptions;
    if (productData?.options) {
      const optionNames = productData.options.map(o => (typeof o === 'string' ? o : o.name || '').toLowerCase());
      const primaryIndex = optionNames.findIndex(name => primaryKeywords.some(k => name.includes(k)));
      if (primaryIndex > 0 && primaryIndex < variantOptions.length) {
        sortedOptions = [...variantOptions];
        const primary = sortedOptions.splice(primaryIndex, 1)[0];
        sortedOptions.unshift(primary);
      }
    }

    // Extra sort: option with swatch image always first (most reliable detection)
    const withSwatch = sortedOptions.filter(o => swatchData[o]);
    const withoutSwatch = sortedOptions.filter(o => !swatchData[o]);
    const finalOrder = [...withSwatch, ...withoutSwatch];

    // Find primary swatch
    const primarySwatch = withSwatch.length ? swatchData[withSwatch[0]] : null;

    // Build pill content based on number of options
    const hasMultipleVariants = (productData?.variants?.length || 0) > 1;
    const hasMultipleOptions = finalOrder.length > 1;

    // Grey out the pill when the selected variant is sold-out so the
     // bundle card clearly signals "out of stock" without extra markup.
    const isSoldOut = variant && variant.available === false;
    let pillHTML = `<span class="cn-pill cn-pill--sm selected bundle-variant-pills__pill bundle-variant-pills__combined${hasMultipleOptions ? '' : ' bundle-variant-pills__combined--single'}${isSoldOut ? ' variant-unavailable' : ''}">`;
    if (primarySwatch) {
      pillHTML += `<img class="pill__swatch" src="${primarySwatch}" alt="" loading="lazy">`;
    }

    if (hasMultipleOptions) {
      // Multiple options: smaak (truncatable) · inhoud (fixed)
      let primaryText = withSwatch.length ? withSwatch[0] : finalOrder[0] || '';
      const secondaryParts = finalOrder.filter(o => o !== primaryText);
      // Truncate smaak name if too long (max 10 chars on mobile)
      const maxLen = window.innerWidth <= 749 ? 10 : 14;
      const displayPrimary = primaryText.length > maxLen ? primaryText.substring(0, maxLen) + '...' : primaryText;
      pillHTML += `<span class="pill__label bundle-variant-pills__primary-label">${displayPrimary}</span>`;
      pillHTML += `<span class="bundle-variant-pills__separator">·</span>`;
      pillHTML += `<span class="pill__label bundle-variant-pills__secondary-label">${secondaryParts.join(' · ')}</span>`;
    } else {
      // Single option: full text, never truncate
      pillHTML += `<span class="pill__label">${finalOrder[0] || ''}</span>`;
    }
    if (hasMultipleVariants) {
      pillHTML += `<svg class="bundle-variant-pills__chevron" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    }
    pillHTML += `</span>`;

    pillsContainer.innerHTML = pillHTML;
    pillsContainer.classList.add('is-visible');

    // Make pill clickable to open variant selector
    pillsContainer.style.cursor = hasMultipleVariants ? 'pointer' : 'default';
    pillsContainer.onclick = hasMultipleVariants ? (e) => {
      e.stopPropagation();
      e.preventDefault();
      const bundleItemEl = pillsContainer.closest('[data-bundle-item]');
      if (!bundleItemEl) return;
      const pd = this.getProductData(bundleItemEl);
      if (pd) this.openVariantSelector(bundleItemEl, pd);
    } : null;
  }

  /**
   * Update total bundle price
   */
  updateTotalPrice() {
    let total = 0;
    let rawTotal = 0;
    this.bundleItems.forEach(item => {
      rawTotal += item.price * item.quantity;
      total += this.applyPrelaunch(item.price, item.prelaunchPct || 0) * item.quantity;
    });

    const discountPercent = parseInt(this.container.dataset.bundleDiscount) || 0;
    const discountedTotal = discountPercent > 0 ? total * (1 - discountPercent / 100) : total;

    const totalElement = this.container.querySelector(this.selectors.totalPrice);
    const compareElement = this.container.querySelector('[data-bundle-compare-price]');

    if (totalElement) {
      totalElement.textContent = this.formatMoney(discountedTotal);
    }

    const showCompare = this.bundleItems.size > 0 && (discountPercent > 0 || rawTotal > total);
    if (compareElement && showCompare) {
      compareElement.textContent = this.formatMoney(rawTotal);
      compareElement.style.display = '';
    } else if (compareElement) {
      compareElement.style.display = 'none';
    }

    const addToCartBtn = this.container.querySelector(this.selectors.addToCartBtn);
    if (addToCartBtn) {
      addToCartBtn.disabled = this.bundleItems.size === 0;
      const countEl = addToCartBtn.querySelector('[data-bundle-item-count]');
      if (countEl) {
        const count = this.bundleItems.size;
        countEl.textContent = count > 0 ? ` (${count} ${count === 1 ? this.t.itemSingular : this.t.itemPlural})` : '';
      }
    }
  }

  /**
   * Adjust summary width to match product grid width on desktop
   */
  adjustSummaryWidth() {
    const summary = this.container.querySelector('.product-bundle__summary');
    if (!summary) return;

    if (window.innerWidth <= 768) {
      summary.style.width = '';
      summary.style.maxWidth = '';
      return;
    }

    const grid = this.container.querySelector('.product-bundle__grid');
    if (!grid) return;

    const bundleItems = grid.querySelectorAll('[data-bundle-item]');
    if (bundleItems.length === 0) return;

    const firstRect = bundleItems[0].getBoundingClientRect();
    const lastRect = bundleItems[bundleItems.length - 1].getBoundingClientRect();
    const gridWidth = lastRect.right - firstRect.left;

    summary.style.width = `${gridWidth}px`;
    summary.style.maxWidth = `${gridWidth}px`;
  }

  /**
   * Add entire bundle to cart
   */
  async addBundleToCart() {
    if (this.bundleItems.size === 0) {
      this.showNotification(this.t.unavailable, 'error');
      return;
    }

    const addToCartBtn = this.container.querySelector(this.selectors.addToCartBtn);
    if (addToCartBtn) {
      addToCartBtn.disabled = true;
      this.container.classList.add('product-bundle--loading');
    }

    try {
      const discountPercent = parseInt(this.container.dataset.bundleDiscount) || 0;
      const bundleId = Date.now().toString();
      const mainProductId = this.container.dataset.mainProductId;
      const sectionId = this.container.dataset.sectionId;

      const items = Array.from(this.bundleItems.values()).map(item => {
        const isMain = String(item.id) === mainProductId;
        const properties = {
          '_bundle': 'true',
          '_bundle_id': bundleId,
          '_bundle_source': mainProductId,
          '_bundle_section': sectionId
        };
        if (!isMain) properties['_upsell'] = 'true';
        if (discountPercent > 0) properties['_bundle_discount'] = `${discountPercent}%`;
        return { id: item.variantId, quantity: item.quantity, properties };
      });

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      await response.json();

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true })
      );

      const cartDrawerButton = document.querySelector('[data-sidebar-button][data-sidebar-id="CartDrawer"]');
      if (cartDrawerButton) cartDrawerButton.click();

      this.showNotification(this.t.cartSuccess, 'success');

    } catch (error) {
      console.error('[Bundle] Error adding to cart:', error);
      this.showNotification(this.t.cartError, 'error');
    } finally {
      if (addToCartBtn) {
        addToCartBtn.disabled = false;
        this.container.classList.remove('product-bundle--loading');
      }
    }
  }

  /**
   * Get product data from bundle item (cached)
   */
  pickRandom(arr, count) {
    const pool = [...arr];
    const result = [];
    while (result.length < count && pool.length > 0) {
      const i = Math.floor(Math.random() * pool.length);
      result.push(pool.splice(i, 1)[0]);
    }
    return result;
  }

  getProductData(bundleItem) {
    const productId = bundleItem.dataset.productId;
    if (this.productDataCache.has(productId)) {
      return this.productDataCache.get(productId);
    }

    const jsonElement = bundleItem.querySelector(this.selectors.productJson);
    if (!jsonElement) return null;

    try {
      const data = JSON.parse(jsonElement.textContent);
      this.productDataCache.set(productId, data);
      return data;
    } catch (error) {
      console.error('[Bundle] Error parsing product data:', error);
      return null;
    }
  }

  /**
   * Pre-launch (early access): percentage dat een premium klant op dit
   * product krijgt. Komt uit Liquid (data-prelaunch-pct op de kaart), 0 voor
   * iedereen buiten de pre-launch. Afkappen op de cent, net als Shopify.
   */
  prelaunchPct(bundleItem) {
    if (!bundleItem) return 0;
    const el = bundleItem.querySelector('[data-prelaunch-pct]');
    return el ? (parseInt(el.dataset.prelaunchPct, 10) || 0) : 0;
  }

  applyPrelaunch(price, pct) {
    if (!pct || pct <= 0 || typeof price !== 'number') return price;
    return price - Math.floor((price * pct) / 100);
  }

  /**
   * Format money using Shopify's money format
   */
  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, theme?.moneyFormat || '€{{amount}}');
    }
    return `€${(cents / 100).toFixed(2)}`;
  }

  showNotification(message, type = 'info') {
    if (typeof window.showNotification === 'function') {
      window.showNotification(message, type);
    }
  }

  // Unchecked checkbox — shown when the product is NOT in the bundle yet.
  getPlusIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="4" width="16" height="16" rx="3" ry="3"></rect></svg>';
  }

  // Checked checkbox (with checkmark) — shown when the product IS in the
  // bundle. Click toggles the state (add ↔ remove).
  getTrashIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="4" width="16" height="16" rx="3" ry="3" fill="currentColor"></rect><polyline points="8 12.5 11 15.5 16.5 9.5" stroke="white"></polyline></svg>';
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.product-bundle').forEach(container => {
    new ProductBundle(container);
  });
});

// Shopify theme editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.product-bundle');
    if (container) new ProductBundle(container);
  });
}
